import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiResponse } from "src/app/core/models/api.model";
import {
  NotificationCategory,
  NotificationDismissResult,
  NotificationMarkAllReadResult,
  NotificationPage,
  NotificationSingleResult,
  NotificationTemplate,
  NotificationUnreadCount,
} from "src/app/core/models/notification.model";

/**
 * Input for {@link NotificationApiService.publish}. `template` is the same
 * NESTED `{ kind, content: {...} }` shape as every other template on the
 * wire — confirmed against flymsg-node's admin create/edit handlers
 * (`adminNotificationController.ts`), which cast the request body's
 * `template` field directly to `NotificationTemplate` and read
 * `template.content` off it when persisting. There is no flat variant
 * anywhere in the contract, incoming or outgoing.
 */
export interface PublishNotificationInput {
  category: NotificationCategory;
  template: NotificationTemplate;
  /** Recipient user id. Omit to target the calling user (self-notify). */
  user_id?: string;
}

/**
 * Thin REST client for flymsg-node's `/api/v3/notifications*` surface
 * (issue #416). Deliberately built on plain `HttpClient` rather than the
 * app's `DataService`: `DataService.apiURL` is hardwired to the Laravel
 * `environment.API_URL`, and its error-handling machinery (plan-limit
 * upsell dialogs, the `DEACTIVATED`-account flow, `updatePlanStore()`
 * side effects) is all flymsg-be-shaped and doesn't apply to this
 * flymsg-node-hosted, unrelated feature. The global `AuthInterceptor`
 * still attaches the Bearer token and handles 401 → refresh → retry for
 * every `HttpClient` request regardless of target host, so nothing here
 * has to duplicate that.
 *
 * Every method here can fail (the backend may not be deployed yet, or a
 * given environment's `NODE_API_URL` may not be configured — see
 * `scripts/set-env.ts`). Callers (`AppNotificationService`) are
 * responsible for degrading silently; this service does not swallow
 * errors itself so failures stay observable in tests.
 */
@Injectable({ providedIn: "root" })
export class NotificationApiService {
  constructor(private readonly http: HttpClient) {}

  list(options: {
    category?: NotificationCategory | null;
    unreadOnly?: boolean;
    page?: number;
    limit?: number;
  }): Observable<ApiResponse<NotificationPage>> {
    let params = new HttpParams();
    if (options.category) {
      params = params.set("category", options.category);
    }
    if (options.unreadOnly) {
      params = params.set("unread_only", "true");
    }
    params = params.set("page", String(options.page ?? 1));
    params = params.set("limit", String(options.limit ?? 20));

    return this.http.get<ApiResponse<NotificationPage>>(this.url("notifications"), { params });
  }

  unreadCount(): Observable<ApiResponse<NotificationUnreadCount>> {
    return this.http.get<ApiResponse<NotificationUnreadCount>>(this.url("notifications/unread-count"));
  }

  markAllRead(): Observable<ApiResponse<NotificationMarkAllReadResult>> {
    return this.http.post<ApiResponse<NotificationMarkAllReadResult>>(this.url("notifications/read-all"), {});
  }

  markRead(id: string): Observable<ApiResponse<NotificationSingleResult>> {
    return this.http.post<ApiResponse<NotificationSingleResult>>(this.url(`notifications/${id}/read`), {});
  }

  dismiss(id: string): Observable<ApiResponse<NotificationDismissResult>> {
    return this.http.post<ApiResponse<NotificationDismissResult>>(this.url(`notifications/${id}/dismiss`), {});
  }

  /**
   * `payload` is stored verbatim — confirmed against
   * `notificationService.respond()` on the backend, which persists
   * `response as unknown as Prisma.InputJsonValue` with no shape
   * validation beyond "not an array". So the client-chosen shapes
   * (`{ action: key }` for `actions`, `{ choices: string[] }` for
   * `survey` — see the two renderer components) are not a wire-contract
   * mismatch the way the inbound `template` shape was; any JSON object
   * round-trips correctly.
   */
  respond(id: string, payload: Record<string, unknown>): Observable<ApiResponse<NotificationSingleResult>> {
    return this.http.post<ApiResponse<NotificationSingleResult>>(this.url(`notifications/${id}/respond`), payload);
  }

  /**
   * Publishes a user-action-originated notification (e.g. a FlyShare share
   * event) so future features integrate with one line (issue #416 requested
   * behavior #3). NOTE: flymsg-node#15 ships only the Vengreso-admin
   * authoring surface (`/api/v3/admin/notifications`, gated on
   * `requireVengresoAdmin`) — there is no user-scoped trigger endpoint yet.
   * This method calls the user-scoped path the rest of `/api/v3/notifications`
   * already follows so callers can integrate against the final shape now;
   * until the backend adds it, every call 404s and `AppNotificationService
   * .publish()` surfaces that as an error rather than pretending it
   * succeeded (see that method's JSDoc).
   */
  publish(input: PublishNotificationInput): Observable<ApiResponse<NotificationSingleResult>> {
    return this.http.post<ApiResponse<NotificationSingleResult>>(this.url("notifications/publish"), input);
  }

  private url(path: string): string {
    return `${environment.NODE_API_URL}/api/v3/${path}`;
  }
}
